import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { formatearFechaLegible } from '../utils/dateUtils';
import * as XLSX from 'xlsx'; 

const ReportesFinancieros = ({ proyectoId, proyecto }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipoReporte, setTipoReporte] = useState('resumen'); // 'resumen', 'graficos', 'detallado'

  useEffect(() => {
    cargarDatos();
  }, [proyectoId]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/proyectos/${proyectoId}/financiero/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

const exportarExcel = () => {
  if (!dashboard) return;

  const XLSX = require('xlsx');

  // Crear un libro de trabajo
  const wb = XLSX.utils.book_new();

  // ═══════════════════════════════════════════════════════════════
  // HOJA 1: Resumen General
  // ═══════════════════════════════════════════════════════════════
  const dataResumen = [
    ['REPORTE FINANCIERO DEL PROYECTO'],
    ['Proyecto:', proyecto?.nombre || ''],
    ['Fecha:', new Date().toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' })],
    [],
    ['RESUMEN GENERAL', ''],
    ['Concepto', 'Valor (RD$)'],
    ['Valor del Contrato', parseFloat(dashboard.proyecto.valor_contrato).toFixed(2)],
    ['Presupuesto Total', parseFloat(dashboard.presupuesto.total).toFixed(2)],
    ['Total Gastado', parseFloat(dashboard.totales.gastado).toFixed(2)],
    ['Disponible', parseFloat(dashboard.totales.disponible).toFixed(2)],
    ['Ganancia Real', parseFloat(dashboard.rentabilidad.ganancia_real).toFixed(2)],
    ['Margen Real (%)', `${dashboard.rentabilidad.margen_real}%`],
    [],
    ['COSTOS DIRECTOS', ''],
    ['Categoría', 'Monto (RD$)'],
    ['Materiales', parseFloat(dashboard.costos_directos.materiales).toFixed(2)],
    ['Mano de Obra', parseFloat(dashboard.costos_directos.mano_obra).toFixed(2)],
    ['Subcontratistas', parseFloat(dashboard.costos_directos.subcontratistas).toFixed(2)],
    ['Subtotal', parseFloat(dashboard.costos_directos.subtotal).toFixed(2)],
    [],
    ['COSTOS INDIRECTOS', ''],
    ['Categoría', 'Monto (RD$)'],
    ['Gastos Operativos', parseFloat(dashboard.costos_indirectos.gastos).toFixed(2)],
    [],
    ['ESTADÍSTICAS DEL PROYECTO', ''],
    ['Concepto', 'Cantidad'],
    ['Empleados Activos', dashboard.estadisticas.empleados_activos],
    ['Subcontratistas Activos', dashboard.estadisticas.subcontratistas_activos],
    ['Jornales Pagados', dashboard.estadisticas.jornales_pagados],
    ['Gastos Registrados', dashboard.estadisticas.gastos_registrados]
  ];

  const wsResumen = XLSX.utils.aoa_to_sheet(dataResumen);
  
  // Ajustar ancho de columnas
  wsResumen['!cols'] = [
    { wch: 35 },
    { wch: 20 }
  ];

  // Estilos para headers y títulos
  const estiloTituloPrincipal = {
    font: { name: 'Arial', sz: 14, bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1E3A8A" } },
    alignment: { horizontal: "center", vertical: "center" }
  };

  const estiloHeader = {
    font: { name: 'Arial', sz: 11, bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "2563EB" } },
    alignment: { horizontal: "center", vertical: "center" }
  };

  const estiloSeccion = {
    font: { name: 'Arial', sz: 12, bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "3B82F6" } },
    alignment: { horizontal: "left", vertical: "center" }
  };

  // Aplicar estilos
  // Título principal (A1)
  wsResumen['A1'].s = estiloTituloPrincipal;
  
  // Headers de secciones
  const headersResumen = ['A6', 'B6', 'A15', 'B15', 'A22', 'B22', 'A26', 'B26'];
  headersResumen.forEach(celda => {
    if (wsResumen[celda]) {
      wsResumen[celda].s = estiloHeader;
    }
  });

  // Títulos de secciones
  const seccionesResumen = ['A5', 'A14', 'A21', 'A25'];
  seccionesResumen.forEach(celda => {
    if (wsResumen[celda]) {
      wsResumen[celda].s = estiloSeccion;
    }
  });

  // Formato de moneda
  const celdasMoneda = ['B7', 'B8', 'B9', 'B10', 'B11', 'B16', 'B17', 'B18', 'B19', 'B23'];
  celdasMoneda.forEach(celda => {
    if (wsResumen[celda]) {
      wsResumen[celda].z = '#,##0.00';
    }
  });

  XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen General');

  // ═══════════════════════════════════════════════════════════════
  // HOJA 2: Presupuesto vs Real
  // ═══════════════════════════════════════════════════════════════
  const dataComparacion = [
    ['PRESUPUESTO VS REAL'],
    [],
    ['Categoría', 'Presupuestado (RD$)', 'Real (RD$)', 'Diferencia (RD$)', '% Ejecutado'],
    [
      '🧱 Materiales',
      parseFloat(dashboard.presupuesto.materiales).toFixed(2),
      parseFloat(dashboard.costos_directos.materiales).toFixed(2),
      parseFloat(dashboard.presupuesto.materiales - dashboard.costos_directos.materiales).toFixed(2),
      `${calcularPorcentaje(dashboard.costos_directos.materiales, dashboard.presupuesto.materiales)}%`
    ],
    [
      '👷 Mano de Obra',
      parseFloat(dashboard.presupuesto.mano_obra).toFixed(2),
      parseFloat(dashboard.costos_directos.mano_obra).toFixed(2),
      parseFloat(dashboard.presupuesto.mano_obra - dashboard.costos_directos.mano_obra).toFixed(2),
      `${calcularPorcentaje(dashboard.costos_directos.mano_obra, dashboard.presupuesto.mano_obra)}%`
    ],
    [
      '💰 Gastos Indirectos',
      parseFloat(dashboard.presupuesto.indirectos).toFixed(2),
      parseFloat(dashboard.costos_indirectos.gastos).toFixed(2),
      parseFloat(dashboard.presupuesto.indirectos - dashboard.costos_indirectos.gastos).toFixed(2),
      `${calcularPorcentaje(dashboard.costos_indirectos.gastos, dashboard.presupuesto.indirectos)}%`
    ],
    [],
    [
      'TOTAL',
      parseFloat(dashboard.presupuesto.total).toFixed(2),
      parseFloat(dashboard.totales.gastado).toFixed(2),
      parseFloat(dashboard.totales.disponible).toFixed(2),
      `${dashboard.totales.porcentaje_gastado}%`
    ]
  ];

  const wsComparacion = XLSX.utils.aoa_to_sheet(dataComparacion);
  
  wsComparacion['!cols'] = [
    { wch: 25 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 15 }
  ];

  // Aplicar estilos a la comparación
  // Título principal
  wsComparacion['A1'].s = estiloTituloPrincipal;

  // Headers
  const headersComparacion = ['A3', 'B3', 'C3', 'D3', 'E3'];
  headersComparacion.forEach(celda => {
    if (wsComparacion[celda]) {
      wsComparacion[celda].s = estiloHeader;
    }
  });

  // Fila de TOTAL con estilo especial
  const totalStyle = {
    font: { name: 'Arial', sz: 11, bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1E40AF" } },
    alignment: { horizontal: "center", vertical: "center" }
  };

  ['A8', 'B8', 'C8', 'D8', 'E8'].forEach(celda => {
    if (wsComparacion[celda]) {
      wsComparacion[celda].s = totalStyle;
    }
  });

  // Formato de moneda para columnas B, C, D
  for (let row = 4; row <= 8; row++) {
    ['B', 'C', 'D'].forEach(col => {
      const celda = `${col}${row}`;
      if (wsComparacion[celda]) {
        wsComparacion[celda].z = '#,##0.00';
      }
    });
  }

  XLSX.utils.book_append_sheet(wb, wsComparacion, 'Presupuesto vs Real');

  // ═══════════════════════════════════════════════════════════════
  // HOJA 3: Análisis de Rentabilidad
  // ═══════════════════════════════════════════════════════════════
 const dataRentabilidad = [
  ['ANÁLISIS DE RENTABILIDAD'],
  [],
  ['Concepto', 'Valor (RD$)'],
  ['Valor del Contrato', parseFloat(dashboard.proyecto.valor_contrato).toFixed(2)],
  ['Presupuesto Total', parseFloat(dashboard.presupuesto.total).toFixed(2)],
  ['Ganancia Estimada', parseFloat(dashboard.rentabilidad.ganancia_estimada || 0).toFixed(2)],
  [],
  ['Total Gastado', parseFloat(dashboard.totales.gastado).toFixed(2)],
  ['Ganancia Proyectada', parseFloat(dashboard.rentabilidad.ganancia_real).toFixed(2)], // CAMBIADO
  ['Margen (%)', `${dashboard.rentabilidad.margen_real}%`],
  [],
  ['Comparación', ''],
  ['Ganancia Estimada', parseFloat(dashboard.rentabilidad.ganancia_estimada || 0).toFixed(2)],
  ['Ganancia Proyectada', parseFloat(dashboard.rentabilidad.ganancia_real).toFixed(2)], // CAMBIADO
  ['Variación', parseFloat(dashboard.rentabilidad.ganancia_real - (dashboard.rentabilidad.ganancia_estimada || 0)).toFixed(2)]
];

  const wsRentabilidad = XLSX.utils.aoa_to_sheet(dataRentabilidad);
  
  wsRentabilidad['!cols'] = [
    { wch: 30 },
    { wch: 20 }
  ];

  // Estilos para rentabilidad
  wsRentabilidad['A1'].s = estiloTituloPrincipal;
  ['A3', 'B3'].forEach(celda => {
    if (wsRentabilidad[celda]) {
      wsRentabilidad[celda].s = estiloHeader;
    }
  });

  // Formato moneda
  ['B4', 'B5', 'B6', 'B8', 'B9', 'B13', 'B14', 'B15'].forEach(celda => {
    if (wsRentabilidad[celda]) {
      wsRentabilidad[celda].z = '#,##0.00';
    }
  });

  XLSX.utils.book_append_sheet(wb, wsRentabilidad, 'Análisis de Rentabilidad');

  // Guardar archivo
  const fecha = new Date().toISOString().split('T')[0];
  const nombreArchivo = `Reporte_Financiero_${proyecto?.nombre.replace(/\s+/g, '_')}_${fecha}.xlsx`;
  XLSX.writeFile(wb, nombreArchivo);

  Swal.fire('✅ Exportado', 'Reporte descargado exitosamente en formato Excel', 'success');
};
  const calcularPorcentaje = (valor, total) => {
    if (total === 0) return 0;
    return ((valor / total) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Cargando reportes...</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div style={styles.empty}>
        <h3>No se pudieron cargar los datos</h3>
      </div>
    );
  }

  const totalCostos = parseFloat(dashboard.totales.gastado);
  const valorContrato = parseFloat(dashboard.proyecto.valor_contrato);
  const presupuestoTotal = parseFloat(dashboard.presupuesto.total);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📊 Reportes Financieros</h2>
          <p style={styles.subtitle}>{proyecto?.nombre}</p>
        </div>
        <button onClick={exportarExcel} style={styles.btnExportar}>
          📥 Exportar a Excel
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(tipoReporte === 'resumen' ? styles.tabActive : {})
          }}
          onClick={() => setTipoReporte('resumen')}
        >
          📋 Resumen
        </button>
        <button
          style={{
            ...styles.tab,
            ...(tipoReporte === 'graficos' ? styles.tabActive : {})
          }}
          onClick={() => setTipoReporte('graficos')}
        >
          📊 Gráficos
        </button>
        <button
          style={{
            ...styles.tab,
            ...(tipoReporte === 'detallado' ? styles.tabActive : {})
          }}
          onClick={() => setTipoReporte('detallado')}
        >
          📑 Detallado
        </button>
      </div>


{/* KPIs Principales */}
<div style={styles.kpisContainer}>
  <div style={styles.kpiCard}>
    <div style={styles.kpiIcono}>💰</div>
    <div>
      <p style={styles.kpiLabel}>Valor del Contrato</p>
      <h3 style={styles.kpiValor}>
        {formatMoney(dashboard.proyecto.valor_contrato)}
      </h3>
    </div>
  </div>

  <div style={styles.kpiCard}>
    <div style={styles.kpiIcono}>📊</div>
    <div>
      <p style={styles.kpiLabel}>Total Gastado</p>
      <h3 style={{...styles.kpiValor, color: '#ef4444'}}>
        {formatMoney(dashboard.totales.gastado)}
      </h3>
      <p style={styles.kpiPorcentaje}>
        {dashboard.totales.porcentaje_gastado}% del presupuesto
      </p>
    </div>
  </div>

  <div style={styles.kpiCard}>
    <div style={styles.kpiIcono}>💵</div>
    <div>
      <p style={styles.kpiLabel}>Ganancia proyectada</p>
      <h3 style={{
        ...styles.kpiValor,
        color: dashboard.rentabilidad.ganancia_estimada >= 0 ? '#10b981' : '#ef4444'
      }}>
        {formatMoney(dashboard.rentabilidad.ganancia_estimada)}
      </h3>
      <p style={styles.kpiPorcentaje}>
        Margen: {dashboard.rentabilidad.margen_real}%
      </p>
    </div>
  </div>

  <div style={styles.kpiCard}>
    <div style={styles.kpiIcono}>📈</div>
    <div>
      <p style={styles.kpiLabel}>Disponible</p>
      <h3 style={{
        ...styles.kpiValor,
        color: dashboard.totales.disponible >= 0 ? '#10b981' : '#ef4444'
      }}>
        {formatMoney(dashboard.totales.disponible)}
      </h3>
      <p style={styles.kpiPorcentaje}>
        {dashboard.totales.porcentaje_gastado}% ejecutado
      </p>
    </div>
  </div>
</div>
      {/* Contenido según tab seleccionado */}
      {tipoReporte === 'resumen' && (
        <div style={styles.contenido}>
          {/* Distribución de Costos */}
          <div style={styles.seccion}>
            <h3 style={styles.seccionTitulo}>💸 Distribución de Costos</h3>
            
            <div style={styles.distribucionItem}>
              <div style={styles.distribucionHeader}>
                <span style={styles.distribucionLabel}>🧱 Materiales</span>
                <span style={styles.distribucionValor}>
                  {formatMoney(dashboard.costos_directos.materiales)}
                </span>
              </div>
              <div style={styles.barraProgreso}>
                <div
                  style={{
                    ...styles.barraFill,
                    width: `${calcularPorcentaje(dashboard.costos_directos.materiales, totalCostos)}%`,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  }}
                ></div>
              </div>
              <span style={styles.porcentajeLabel}>
                {calcularPorcentaje(dashboard.costos_directos.materiales, totalCostos)}%
              </span>
            </div>

            <div style={styles.distribucionItem}>
              <div style={styles.distribucionHeader}>
                <span style={styles.distribucionLabel}>👷 Mano de Obra</span>
                <span style={styles.distribucionValor}>
                  {formatMoney(dashboard.costos_directos.mano_obra)}
                </span>
              </div>
              <div style={styles.barraProgreso}>
                <div
                  style={{
                    ...styles.barraFill,
                    width: `${calcularPorcentaje(dashboard.costos_directos.mano_obra, totalCostos)}%`,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  }}
                ></div>
              </div>
              <span style={styles.porcentajeLabel}>
                {calcularPorcentaje(dashboard.costos_directos.mano_obra, totalCostos)}%
              </span>
            </div>

            <div style={styles.distribucionItem}>
              <div style={styles.distribucionHeader}>
                <span style={styles.distribucionLabel}>🔧 Subcontratistas</span>
                <span style={styles.distribucionValor}>
                  {formatMoney(dashboard.costos_directos.subcontratistas)}
                </span>
              </div>
              <div style={styles.barraProgreso}>
                <div
                  style={{
                    ...styles.barraFill,
                    width: `${calcularPorcentaje(dashboard.costos_directos.subcontratistas, totalCostos)}%`,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  }}
                ></div>
              </div>
              <span style={styles.porcentajeLabel}>
                {calcularPorcentaje(dashboard.costos_directos.subcontratistas, totalCostos)}%
              </span>
            </div>

            <div style={styles.distribucionItem}>
              <div style={styles.distribucionHeader}>
                <span style={styles.distribucionLabel}>💰 Gastos Indirectos</span>
                <span style={styles.distribucionValor}>
                  {formatMoney(dashboard.costos_indirectos.gastos)}
                </span>
              </div>
              <div style={styles.barraProgreso}>
                <div
                  style={{
                    ...styles.barraFill,
                    width: `${calcularPorcentaje(dashboard.costos_indirectos.gastos, totalCostos)}%`,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  }}
                ></div>
              </div>
              <span style={styles.porcentajeLabel}>
                {calcularPorcentaje(dashboard.costos_indirectos.gastos, totalCostos)}%
              </span>
            </div>
          </div>

          {/* Comparación Presupuesto vs Real */}
          <div style={styles.seccion}>
            <h3 style={styles.seccionTitulo}>📊 Presupuesto vs Real</h3>
            
            <div style={styles.comparacionGrid}>
              <div style={styles.comparacionItem}>
                <div style={styles.comparacionLabel}>Materiales</div>
                <div style={styles.comparacionValores}>
                  <div>
                    <span style={styles.valorLabel}>Presupuesto:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.presupuesto.materiales)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Real:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.costos_directos.materiales)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Diferencia:</span>
                    <span style={{
                      ...styles.valorCantidad,
                      color: (dashboard.presupuesto.materiales - dashboard.costos_directos.materiales) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatMoney(dashboard.presupuesto.materiales - dashboard.costos_directos.materiales)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.comparacionItem}>
                <div style={styles.comparacionLabel}>Mano de Obra</div>
                <div style={styles.comparacionValores}>
                  <div>
                    <span style={styles.valorLabel}>Presupuesto:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.presupuesto.mano_obra)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Real:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.costos_directos.mano_obra)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Diferencia:</span>
                    <span style={{
                      ...styles.valorCantidad,
                      color: (dashboard.presupuesto.mano_obra - dashboard.costos_directos.mano_obra) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatMoney(dashboard.presupuesto.mano_obra - dashboard.costos_directos.mano_obra)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.comparacionItem}>
                <div style={styles.comparacionLabel}>Gastos Indirectos</div>
                <div style={styles.comparacionValores}>
                  <div>
                    <span style={styles.valorLabel}>Presupuesto:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.presupuesto.indirectos)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Real:</span>
                    <span style={styles.valorCantidad}>{formatMoney(dashboard.costos_indirectos.gastos)}</span>
                  </div>
                  <div>
                    <span style={styles.valorLabel}>Diferencia:</span>
                    <span style={{
                      ...styles.valorCantidad,
                      color: (dashboard.presupuesto.indirectos - dashboard.costos_indirectos.gastos) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatMoney(dashboard.presupuesto.indirectos - dashboard.costos_indirectos.gastos)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div style={styles.seccion}>
            <h3 style={styles.seccionTitulo}>📈 Estadísticas del Proyecto</h3>
            
            <div style={styles.estadisticasGrid}>
              <div style={styles.estadCard}>
                <div style={styles.estadIcono}>👥</div>
                <div style={styles.estadValor}>{dashboard.estadisticas.empleados_activos}</div>
                <div style={styles.estadLabel}>Empleados Activos</div>
              </div>

              <div style={styles.estadCard}>
                <div style={styles.estadIcono}>🔧</div>
                <div style={styles.estadValor}>{dashboard.estadisticas.subcontratistas_activos}</div>
                <div style={styles.estadLabel}>Subcontratistas</div>
              </div>

              <div style={styles.estadCard}>
                <div style={styles.estadIcono}>📅</div>
                <div style={styles.estadValor}>{dashboard.estadisticas.jornales_pagados}</div>
                <div style={styles.estadLabel}>Jornales Pagados</div>
              </div>

              <div style={styles.estadCard}>
                <div style={styles.estadIcono}>💸</div>
                <div style={styles.estadValor}>{dashboard.estadisticas.gastos_registrados}</div>
                <div style={styles.estadLabel}>Gastos Registrados</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tipoReporte === 'graficos' && (
        <div style={styles.contenido}>
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>📊</div>
            <h3>Gráficos Avanzados</h3>
            <p>Funcionalidad disponible con librerías de gráficos (Chart.js, Recharts)</p>
            <p style={{marginTop: '10px', fontSize: '14px', color: '#64748b'}}>
              Los datos están listos para ser visualizados con cualquier librería de gráficos
            </p>
          </div>
        </div>
      )}

      {tipoReporte === 'detallado' && (
        <div style={styles.contenido}>
          {/* Tabla Detallada */}
          <div style={styles.seccion}>
            <h3 style={styles.seccionTitulo}>📑 Reporte Detallado</h3>
            
            <div style={styles.tabla}>
              <div style={styles.tablaHeader}>
                <div style={styles.tablaHeaderCell}>Categoría</div>
                <div style={styles.tablaHeaderCell}>Presupuestado</div>
                <div style={styles.tablaHeaderCell}>Real</div>
                <div style={styles.tablaHeaderCell}>Diferencia</div>
                <div style={styles.tablaHeaderCell}>% Ejecutado</div>
              </div>

              <div style={styles.tablaRow}>
                <div style={styles.tablaCell}>🧱 Materiales</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.presupuesto.materiales)}</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.costos_directos.materiales)}</div>
                <div style={{
                  ...styles.tablaCell,
                  color: (dashboard.presupuesto.materiales - dashboard.costos_directos.materiales) >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.presupuesto.materiales - dashboard.costos_directos.materiales)}
                </div>
                <div style={styles.tablaCell}>
                  {calcularPorcentaje(dashboard.costos_directos.materiales, dashboard.presupuesto.materiales)}%
                </div>
              </div>

              <div style={styles.tablaRow}>
                <div style={styles.tablaCell}>👷 Mano de Obra</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.presupuesto.mano_obra)}</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.costos_directos.mano_obra)}</div>
                <div style={{
                  ...styles.tablaCell,
                  color: (dashboard.presupuesto.mano_obra - dashboard.costos_directos.mano_obra) >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.presupuesto.mano_obra - dashboard.costos_directos.mano_obra)}
                </div>
                <div style={styles.tablaCell}>
                  {calcularPorcentaje(dashboard.costos_directos.mano_obra, dashboard.presupuesto.mano_obra)}%
                </div>
              </div>

              <div style={styles.tablaRow}>
                <div style={styles.tablaCell}>💰 Gastos Indirectos</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.presupuesto.indirectos)}</div>
                <div style={styles.tablaCell}>{formatMoney(dashboard.costos_indirectos.gastos)}</div>
                <div style={{
                  ...styles.tablaCell,
                  color: (dashboard.presupuesto.indirectos - dashboard.costos_indirectos.gastos) >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.presupuesto.indirectos - dashboard.costos_indirectos.gastos)}
                </div>
                <div style={styles.tablaCell}>
                  {calcularPorcentaje(dashboard.costos_indirectos.gastos, dashboard.presupuesto.indirectos)}%
                </div>
              </div>

              <div style={{...styles.tablaRow, background: '#f8fafc', fontWeight: '700'}}>
                <div style={styles.tablaCell}>TOTAL</div>
                <div style={styles.tablaCell}>{formatMoney(presupuestoTotal)}</div>
                <div style={styles.tablaCell}>{formatMoney(totalCostos)}</div>
                <div style={{
                  ...styles.tablaCell,
                  color: dashboard.totales.disponible >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.totales.disponible)}
                </div>
                <div style={styles.tablaCell}>
                  {dashboard.totales.porcentaje_gastado}%
                </div>
              </div>
            </div>
          </div>

          {/* Análisis de Rentabilidad */}
          <div style={styles.seccion}>
            <h3 style={styles.seccionTitulo}>💹 Análisis de Rentabilidad</h3>
            
            <div style={styles.rentabilidadGrid}>
              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Valor del Contrato</div>
                <div style={styles.rentabilidadValor}>{formatMoney(valorContrato)}</div>
              </div>

              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Presupuesto Total</div>
                <div style={styles.rentabilidadValor}>{formatMoney(presupuestoTotal)}</div>
              </div>

              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Ganancia Estimada</div>
                <div style={{
                  ...styles.rentabilidadValor,
                  color: dashboard.rentabilidad.ganancia_estimada >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.rentabilidad.ganancia_estimada)}
                </div>
              </div>

              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Total Gastado</div>
                <div style={{...styles.rentabilidadValor, color: '#ef4444'}}>
                  {formatMoney(totalCostos)}
                </div>
              </div>

              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Gasto total</div>
                <div style={{
                  ...styles.rentabilidadValor,
                  color: dashboard.rentabilidad.ganancia_real >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatMoney(dashboard.rentabilidad.ganancia_real)}
                </div>
              </div>

              <div style={styles.rentabilidadCard}>
                <div style={styles.rentabilidadLabel}>Margen Real</div>
                <div style={{
                  ...styles.rentabilidadValor,
                  color: dashboard.rentabilidad.margen_real >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {dashboard.rentabilidad.margen_real}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnExportar: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #e2e8f0'
  },
  tab: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#3b82f6',
    borderBottom: '3px solid #3b82f6'
  },
  kpisContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '30px'
  },
  kpiCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  kpiIcono: {
    fontSize: '40px'
  },
  kpiLabel: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 5px 0',
    fontWeight: '600'
  },
  kpiValor: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0'
  },
  kpiPorcentaje: {
    fontSize: '12px',
    color: '#64748b',
    margin: 0
  },
  contenido: {
    display: 'grid',
    gap: '20px'
  },
  seccion: {
    background: 'white',
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  seccionTitulo: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px'
  },
  distribucionItem: {
    marginBottom: '20px'
  },
  distribucionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  distribucionLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569'
  },
  distribucionValor: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1e293b'
  },
  barraProgreso: {
    height: '12px',
    background: '#e5e7eb',
    borderRadius: '999px',
    overflow: 'hidden',
    marginBottom: '5px'
  },
  barraFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.3s'
  },
  porcentajeLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600'
  },
  comparacionGrid: {
    display: 'grid',
    gap: '20px'
  },
  comparacionItem: {
    padding: '15px',
    background: '#f8fafc',
    borderRadius: '8px'
  },
  comparacionLabel: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '10px'
  },
  comparacionValores: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px'
  },
  valorLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginRight: '8px'
  },
  valorCantidad: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b'
  },
  estadisticasGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px'
  },
  estadCard: {
    textAlign: 'center',
    padding: '20px',
    background: '#f8fafc',
    borderRadius: '8px'
  },
  estadIcono: {
    fontSize: '32px',
    marginBottom: '10px'
  },
  estadValor: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '5px'
  },
  estadLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '600'
  },
  tabla: {
    overflow: 'auto'
  },
  tablaHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 1fr',
    gap: '10px',
    padding: '15px',
    background: '#f1f5f9',
    borderRadius: '8px 8px 0 0',
    fontWeight: '700',
    fontSize: '13px',
    color: '#475569'
  },
  tablaHeaderCell: {
    padding: '0 10px'
  },
  tablaRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 1fr',
    gap: '10px',
    padding: '15px',
    borderBottom: '1px solid #e2e8f0'
  },
  tablaCell: {
    fontSize: '14px',
    color: '#1e293b',
    padding: '0 10px'
  },
  rentabilidadGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px'
  },
  rentabilidadCard: {
    padding: '20px',
    background: '#f8fafc',
    borderRadius: '8px',
    textAlign: 'center'
  },
  rentabilidadLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '600',
    marginBottom: '10px'
  },
  rentabilidadValor: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 15px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px'
  }
};

export default ReportesFinancieros;